import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, UploadCloud } from 'lucide-react';
import { uploadApi, validateMediaFile, FILE_ACCEPT_STRINGS } from '../../../api/upload.api';
import { useUIStore } from '../../../store';

const BotMetaMediaPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [mediaHandle, setMediaHandle] = useState('');
  const [status, setStatus] = useState('');
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(file);
    setPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  useEffect(() => {
    if (!status) return;
    showSnackbar({ message: status, severity: 'success' });
    setStatus('');
  }, [showSnackbar, status]);

  const fileLabel = useMemo(() => {
    if (!file) return 'No file selected';
    return `${file.name} (${Math.round(file.size / 1024)} KB)`;
  }, [file]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) return;
    const validation = validateMediaFile(selected, 'all');
    if (!validation.valid) {
      setError(validation.error || 'Unsupported file type.');
      setFile(null);
      setMediaHandle('');
      setStatus('');
      return;
    }
    setError('');
    setStatus('');
    setMediaHandle('');
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setProgress(0);
    setError('');
    setStatus('');
    try {
      const response = await uploadApi.uploadMedia(file, (percent) => {
        setProgress(percent);
      });
      const payload = response?.data?.data ?? response?.data ?? {};
      const handle = payload?.handle || payload?.media_id || payload?.id || payload?.url || '';
      if (!handle) {
        throw new Error('Upload failed: missing handle.');
      }
      setMediaHandle(handle);
      setStatus('Upload complete.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!mediaHandle) return;
    try {
      await navigator.clipboard.writeText(mediaHandle);
      setStatus('Handle copied to clipboard.');
    } catch (err) {
      setError('Copy failed. Please copy manually.');
    }
  };

  const handleClear = () => {
    setFile(null);
    setMediaHandle('');
    setStatus('');
    setError('');
    setProgress(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/bots')}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Meta Media Upload
        </Typography>
      </Stack>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <Typography color="text.secondary">
              Upload media via Meta (non-resumable). A media handle is returned.
            </Typography>

            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}


            <Stack spacing={1}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadCloud size={18} />}
                disabled={uploading}
                sx={{ alignSelf: 'flex-start' }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept={FILE_ACCEPT_STRINGS.all}
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {fileLabel}
              </Typography>
              {previewUrl && (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              )}
            </Stack>

            <Stack spacing={1}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Media'}
              </Button>
              {uploading && (
                <Stack spacing={1}>
                  <LinearProgress
                    variant={progress === null ? 'indeterminate' : 'determinate'}
                    value={progress === null ? 0 : progress}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      {progress === null ? 'Uploading...' : `Uploading ${progress}%`}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Media Handle</Typography>
              <TextField
                value={mediaHandle}
                placeholder="Upload a file to generate a handle"
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Copy size={16} />}
                  onClick={handleCopy}
                  disabled={!mediaHandle}
                >
                  Copy Handle
                </Button>
                <Button variant="text" onClick={handleClear} disabled={uploading && !mediaHandle}>
                  Clear
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BotMetaMediaPage;
